import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { AuthSelectors, AuthState } from "@app/auth/store";
import { ClareServicePlan, ProjectView, ProjectViewHelper } from "@app/project/model";
import { Store, select } from "@ngrx/store";
import { Observable, Subject, combineLatest, merge, of } from "rxjs";
import { exhaustMap, filter, map, take, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { AccountSearchFilter, AccountSearchRequest } from "../account-search/account-search.component";

import { ADD_ACCOUNT } from "@app/app-routing.constants";
import { AuthUser } from "@app/auth/model/auth-user";
import { ALL_VIEW_COLUMNS } from "@app/project/constants/all-view-columns.constants";
import { ServicePlanTiers } from "@app/project/model/service-plan";
import { accountRoutePaths } from "@app/project/project-routing-constants";
import { ProjectStatusService } from "@app/project/services/project-status.service";
import { ServicePlanUtils } from "@app/project/services/service-plan-utils";
import { ServicePlansService } from "@app/project/services/service-plans.service";
import { ProjectViewDispatchers } from "@app/project/store/dispatchers/project-view.dispatchers";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { ProjectStatus } from "@app/project/store/selectors/projects.selectors";
import { RouterActions } from "@app/root-store";
import { ClareStatusColors } from "@app/shared/model/clare-status.model";
import { StatusCountService } from "@app/shared/services/status-count.service";
import { LoadingState } from "@app/root-store/api-result.state";
import { ProjectActions } from "@app/project/store";
import { NgxPermissionsService } from "ngx-permissions";

@Component({
    selector: "app-dashboard-home",
    templateUrl: "./home.component.html",
    styleUrls: ["./home.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardHomeComponent implements OnInit, OnDestroy {
    user$: Observable<AuthUser>;
    unsubscribe$ = new Subject<null>();

    addAccountRoute = `/${ADD_ACCOUNT}`;
    accountListRoute = "/" + accountRoutePaths.root;
    accountSearchFilters: AccountSearchFilter[] = [];

    filters: AccountSearchFilter[] = [];

    // NOTE: Don't rely on type checker to catch typos when assigning to this obj
    //       https://stackoverflow.com/questions/70356340/
    totals = {
        accounts: 0,
        [ProjectStatus.OFFLINE]: 0,
        [ProjectStatus.UNCONFIGURED]: 0,
        [ProjectStatus.ONLINE]: 0,

        paidServicePlans: 0,
        [ServicePlanTiers.INTERACTIVE_PLUS_LTE]: 0,
        [ServicePlanTiers.INTERACTIVE_PLUS]: 0,
        [ServicePlanTiers.INTERACTIVE]: 0,
        [ServicePlanTiers.BASIC]: 0,
    };
    servicePlanTiers = ServicePlanTiers;
    servicePlans: ClareServicePlan[] = [];

    private hasViewSuspendedAccountsPermission = false;

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly projectDispatchers: ProjectDispatchers,
        private readonly projectStatusService: ProjectStatusService,
        private readonly projectViewDispatcher: ProjectViewDispatchers,
        private readonly statusCountService: StatusCountService,
        private readonly store: Store<AuthState>,
        private readonly servicePlanService: ServicePlansService,
        private readonly permissionsService: NgxPermissionsService
    ) {}

    ngOnInit() {
        this.user$ = this.store.pipe(
            select(AuthSelectors.selectGetUser),
            filter(user => !!user)
        );

        combineLatest([this.permissionsService.permissions$, this.user$])
            .pipe(
                takeUntil(this.unsubscribe$),
                filter(([permissions, authUser]) => !!permissions && !!authUser)
            )
            .subscribe(([permissions, authUser]) => {
                this.hasViewSuspendedAccountsPermission = !!permissions["viewArchivedProjects"];
                this.projectDispatchers.dispatch(
                    new ProjectActions.GetProjects({
                        entityId: authUser.entityId,
                        projectView: ProjectViewHelper.getDefaultViewForRole(authUser),
                    })
                );
                this.initTotals$().subscribe();
                this.initFilters$().subscribe();
            });

        this.servicePlanService
            .getServicePlans()
            .pipe(
                take(1),
                tap(plans => {
                    this.servicePlans = plans;
                })
            )
            .subscribe();
    }

    onSearch(request: AccountSearchRequest) {
        const queryParams = { query: request.query };

        const colsToSearch = Object.entries(request.filters)
            .filter(([_, isActive]) => isActive)
            .map(([name, _]) => name);
        if (colsToSearch.length) {
            queryParams["columns"] = colsToSearch.join(",");
        }

        this.store.dispatch(RouterActions.routerGo({ path: [this.accountListRoute], query: queryParams }));
    }

    private initTotals$() {
        let projectStatusCountsObs$: Observable<Record<ProjectStatus, number>>;

        if (this.hasViewSuspendedAccountsPermission) {
            projectStatusCountsObs$ = this.projectStatusService.selectProjectStatusCounts$();
        } else {
            projectStatusCountsObs$ = this.projectStatusService.selectActiveProjectStatusCounts$();
        }

        const tallyStatus$ = this.projectDispatchers.loadAuthUserProjectsUUIDs$().pipe(
            exhaustMap(() =>
                this.projectStatusService.getProjectStatuses$().pipe(
                    filter(res => res.callState === LoadingState.loaded),
                    take(1)
                )
            ),
            exhaustMap(() => projectStatusCountsObs$.pipe(takeUntil(this.unsubscribe$))),
            map(statuses => this.statusCountService.getStatusCounts(statuses)),
            tap(counts => {
                this.totals.accounts = counts.totalCount;
                counts.statusItems.forEach(item => {
                    this.totals[item.label] = item.itemCount;
                });
            })
        );

        let servicePlanSummaryObs$: Observable<Record<string, number>>;

        if (this.hasViewSuspendedAccountsPermission) {
            servicePlanSummaryObs$ = this.projectStatusService.selectProjectServicePlansCounts$();
        } else {
            servicePlanSummaryObs$ = this.projectStatusService.selectActiveProjectServicePlansCounts$();
        }

        const tallyPlans$ = servicePlanSummaryObs$.pipe(
            tap(plans => {
                this.totals = {
                    ...this.totals,
                    paidServicePlans: 0,
                    [ServicePlanTiers.INTERACTIVE_PLUS_LTE]: 0,
                    [ServicePlanTiers.INTERACTIVE_PLUS]: 0,
                    [ServicePlanTiers.INTERACTIVE]: 0,
                    [ServicePlanTiers.BASIC]: 0,
                };
                // Process service plans
                this.totals.paidServicePlans = 0;
                Object.entries(plans).forEach(([key, count]) => {
                    // Treat accounts without a plan as if they're Basic
                    const servicePlanKey = key || ServicePlanTiers.BASIC;

                    if (!this.totals.hasOwnProperty(servicePlanKey)) {
                        // Plans are coming from API so if we get here, someone probably forgot to update the ServicePlanTiers enum
                        console.error("Unexpected service plan:", servicePlanKey);
                        return;
                    }

                    this.totals[servicePlanKey] += count;

                    if (servicePlanKey !== ServicePlanTiers.BASIC) {
                        this.totals.paidServicePlans += count;
                    }
                });
            })
        );

        return merge(tallyStatus$, tallyPlans$).pipe(
            tap(() => {
                this.cdr.detectChanges();
            }),
            takeUntil(this.unsubscribe$)
        );
    }

    /**
     * Set this.filters to whatever view the user selected
     */
    private initFilters$() {
        const projectViews$ = this.user$.pipe(
            exhaustMap(authUser => this.projectViewDispatcher.getProjectViews$(authUser.entityId, authUser.id)),
            take(1)
        );

        // View that the user explicitly selected this session
        const sessionViewId = this.projectViewDispatcher.getCurrentProjectFilter().view;
        const sessionView$: Observable<ProjectView | undefined> = projectViews$.pipe(map(views => views.find(v => v.id === sessionViewId)));

        // Custom view that was set as default
        const userDefaultView$: Observable<ProjectView | undefined> = projectViews$.pipe(
            withLatestFrom(this.user$),
            map(([projectViews, authUser]) => projectViews.filter(v => v.isDefault).find(v => v.userinfoId === authUser.id))
        );

        // Default view for role
        const roleDefaultView$: Observable<ProjectView> = this.user$.pipe(
            map(authUser => ProjectViewHelper.getDefaultViewForRole(authUser))
        );

        // Select one of [sessionView, userDefaultView, roleDefaultView], in that order
        const activeView$: Observable<ProjectView> = sessionView$.pipe(
            exhaustMap(view => (view ? of(view) : userDefaultView$)),
            exhaustMap(view => (view ? of(view) : roleDefaultView$))
        );

        // Set this.filters to the column names of activeView$
        return activeView$.pipe(
            tap(view => {
                const filtersUpdate: AccountSearchFilter[] = view.viewData.map(colId => {
                    // const col: PropertyObject = ALL_VIEW_COLUMNS.find(prop => prop.viewKey === colId);
                    let colName = ALL_VIEW_COLUMNS[colId]?.viewHeader;
                    if (!colName) {
                        console.error("Unknown account view column:", colId);
                        colName = colId;
                    }

                    return { key: colId, display: colName };
                });
                this.filters = filtersUpdate;
            }),
            take(1)
        );
    }

    getDisplayName(user: AuthUser): string {
        const firstLast = user.firstName + " " + user.lastName;
        return user?.displayName || firstLast;
    }

    getServicePlanColor(tier: ServicePlanTiers): string {
        const servicePlan = this.servicePlans.find(p => p.name === tier);
        return ServicePlanUtils.smallIconColor(servicePlan);
    }

    getOnlineStatusColor(onlineStatus) {
        const statusColors = {
            online: ClareStatusColors.Online,
            offline: ClareStatusColors.Offline,
            default: ClareStatusColors.Unconfigured,
        };

        return statusColors[onlineStatus] || statusColors.default;
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
